import { useCallback, useEffect, useRef } from "react";
import { useApolloClient, useMutation } from "@apollo/client";
import { get } from "lodash";
import { useTranslation } from "react-i18next";
import { NAME_SPACES } from "shared/locales/constants";
import graphql from "utils/api/graphql";
import { resolveApiErrorMessage } from "utils/helpers/graphql-errors";

const POLL_INTERVAL = 2000;
const POLL_TIMEOUT = 5 * 60 * 1000;

const MAX_CONSECUTIVE_POLL_ERRORS = 3;

const STATUS = {
  DONE: "DONE",
  FAILED: "FAILED",
};

const JOB_ERROR = {
  NOT_STARTED: "NO_JOB",
  LOST: "JOB_LOST",
  TIMEOUT: "TIMEOUT",
  EMPTY: "EMPTY",
};

const JOBS = {
  HTML: { ticket: "startScreenHtmlFromImage", result: "html" },
  STATE: { ticket: "startScreenStateFromImage", result: "state" },
  REVISION: { ticket: "startScreenRevision", result: "proposal" },
};

const sleep = (ms) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

const useScreenJob = ({ active = true } = {}) => {
  const client = useApolloClient();
  const { t } = useTranslation(NAME_SPACES.ERRORS);
  const catalogue = t("API", { returnObjects: true });
  const apiErrors = useRef(catalogue);
  useEffect(() => {
    apiErrors.current = catalogue;
  }, [catalogue]);

  const alive = useRef(true);
  useEffect(() => {
    alive.current = active;
  }, [active]);
  useEffect(
    () => () => {
      alive.current = false;
    },
    [],
  );

  const described = useCallback((error) => {
    const reason = resolveApiErrorMessage(error, apiErrors.current);
    if (reason && reason !== apiErrors.current.GENERIC) error.reason = reason;
    return error;
  }, []);

  const [startHtml] = useMutation(
    graphql.mutations.START_SCREEN_HTML_FROM_IMAGE,
  );
  const [startState] = useMutation(
    graphql.mutations.START_SCREEN_STATE_FROM_IMAGE,
  );
  const [startRevision] = useMutation(graphql.mutations.START_SCREEN_REVISION);

  const poll = useCallback(
    async (id, field) => {
      const deadline = Date.now() + POLL_TIMEOUT;
      let errors = 0;
      while (alive.current && Date.now() < deadline) {
        await sleep(POLL_INTERVAL);
        if (!alive.current) return null;
        let job;
        try {
          const answer = await client.query({
            query: graphql.queries.SCREEN_GENERATION_JOB,
            variables: { id },
            fetchPolicy: "no-cache",
          });
          job = get(answer, "data.screenGenerationJob");
          if (!job) throw new Error(JOB_ERROR.LOST);
          errors = 0;
        } catch (error) {
          errors += 1;
          if (errors >= MAX_CONSECUTIVE_POLL_ERRORS) throw error;
          continue;
        }
        if (!alive.current) return null;
        if (job.status === STATUS.FAILED) {
          throw new Error(job.error || JOB_ERROR.EMPTY);
        }
        if (job.status === STATUS.DONE) {
          const result = get(job, field);
          if (!result) throw new Error(JOB_ERROR.EMPTY);
          return result;
        }
      }
      if (!alive.current) return null;
      throw new Error(JOB_ERROR.TIMEOUT);
    },
    [client],
  );

  const run = useCallback(
    async (start, variables, kind) => {
      try {
        const started = await start({ variables });
        const id = get(started, `data.${kind.ticket}.id`);
        if (!id) throw new Error(JOB_ERROR.NOT_STARTED);
        return await poll(id, kind.result);
      } catch (error) {
        if (!alive.current) return null;
        throw described(error);
      }
    },
    [poll, described],
  );

  const generateHtml = useCallback(
    (variables) => run(startHtml, variables, JOBS.HTML),
    [run, startHtml],
  );
  const generateState = useCallback(
    (variables) => run(startState, variables, JOBS.STATE),
    [run, startState],
  );
  const revise = useCallback(
    (variables) => run(startRevision, variables, JOBS.REVISION),
    [run, startRevision],
  );

  return { generateHtml, generateState, revise };
};

export default useScreenJob;
